<template>
    <v-dialog v-model="openMode" max-width="820px" content-class="mm-dialog" overlay-color="#0d2c6d"
        overlay-opacity="0.55" transition="dialog-bottom-transition" :persistent="progressShow">
        <v-card class="mm-dlg-card">
            <!-- ─────────────────── HEADER ─────────────────── -->
            <div class="mm-dlg-head">
                <div class="mm-dlg-head__main">
                    <div class="mm-dlg-head__icon" aria-hidden="true">
                        <v-icon color="white">mdi-cellphone-cog</v-icon>
                    </div>
                    <div>
                        <span class="mm-dlg-eyebrow">פעולות מול ספק סלולר</span>
                        <h2 class="mm-dlg-title">
                            {{ currentStep === 'select' ? 'בחר פעולה לביצוע' : currentActionConfig.title }}
                        </h2>
                    </div>
                </div>
                <div class="mm-dlg-head__pill" v-if="operatorName">
                    <v-icon small color="white">mdi-access-point</v-icon>
                    {{ operatorName }}
                </div>
                <button type="button" class="mm-dlg-close" @click="closeDialog" aria-label="סגור">
                    <v-icon small color="white">mdi-close</v-icon>
                </button>
            </div>

            <!-- ─────────────────── BREADCRUMB (only in action step) ─────────────────── -->
            <div class="oa-breadcrumb" v-if="currentStep === 'action'">
                <button type="button" class="oa-back-btn" @click="backToSelect" :disabled="progressShow">
                    <v-icon size="16">mdi-arrow-right</v-icon>
                    חזרה לרשימת הפעולות
                </button>
                <div class="oa-breadcrumb__manui">
                    <v-icon size="14" color="#94a3b8">mdi-sim</v-icon>
                    <span>מנוי: {{ itemEdited.manui_number || '—' }}</span>
                </div>
            </div>

            <!-- ─────────────────── BODY ─────────────────── -->
            <v-card-text class="mm-dlg-body">

                <!-- ============ STEP 1: action selection ============ -->
                <div v-if="currentStep === 'select'" class="oa-select">
                    <div class="oa-info-banner">
                        <div class="oa-info-banner__icon">
                            <v-icon size="18" color="#3F51B5">mdi-information-outline</v-icon>
                        </div>
                        <div class="oa-info-banner__text">
                            הפעולות הבאות מבוצעות ישירות מול ספק הסלולר. חלק מהפעולות מבוצעות באופן
                            מיידי ואחרות ממתינות לאישור הספק.
                        </div>
                    </div>

                    <div v-if="hasCurrentSimIssue" class="oa-warning-banner">
                        <div class="oa-warning-banner__icon">
                            <v-icon size="20" color="#c0691c">mdi-alert-outline</v-icon>
                        </div>
                        <div class="oa-warning-banner__content">
                            <div class="oa-warning-banner__title">{{ currentSimInvalidReason }}</div>
                            <div class="oa-warning-banner__text">
                                פעולות הדורשות את הסים הנוכחי לא יהיו זמינות. ניתן לעדכן את מספר הסים
                                במסך עדכון מספר סים לפני ביצוע הפעולה.
                            </div>
                        </div>
                    </div>

                    <div class="oa-actions-grid">
                        <button v-for="action in availableActions" :key="action.key" type="button"
                            class="oa-action-card" :class="{ 'oa-action-card--disabled': !action.enabled }"
                            :disabled="!action.enabled" @click="selectAction(action)">
                            <div class="oa-action-card__icon"
                                :style="{ background: action.iconBg, color: action.iconColor }">
                                <v-icon :size="22" :color="action.iconColor">{{ action.icon }}</v-icon>
                            </div>
                            <div class="oa-action-card__body">
                                <div class="oa-action-card__head">
                                    <span class="oa-action-card__title">{{ action.title }}</span>
                                    <span v-if="!action.enabled"
                                        class="oa-action-card__badge oa-action-card__badge--disabled">
                                        {{ action.disabledReason }}
                                    </span>
                                    <template v-else>
                                        <span class="oa-action-card__badge oa-action-card__badge--enabled">
                                            <span class="oa-action-card__badge-dot"></span>
                                            זמין
                                        </span>
                                        <span v-if="action.responseMode === 'immediate'"
                                            class="oa-action-card__badge oa-action-card__badge--immediate">
                                            <v-icon size="11" color="#0a7a3a">mdi-lightning-bolt</v-icon>
                                            מיידי
                                        </span>
                                        <span v-else-if="action.responseMode === 'callback'"
                                            class="oa-action-card__badge oa-action-card__badge--callback">
                                            <v-icon size="11" color="#5a4ab8">mdi-clock-outline</v-icon>
                                            הבקשה נשלחת לביצוע, ההחלפה אינה מיידית
                                        </span>
                                    </template>
                                </div>
                                <span class="oa-action-card__desc">{{ action.description }}</span>
                            </div>
                            <div class="oa-action-card__chev" v-if="action.enabled">
                                <v-icon size="18">mdi-chevron-left</v-icon>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- ============ STEP 2A: SIM replacement form ============ -->
                <div v-else-if="currentStep === 'action' && currentAction === 'replace_sim'" class="mm-dlg-section">
                    <h3 class="mm-dlg-section__title">
                        <span class="mm-dlg-section__bar"></span>
                        החלפת סים אצל הספק
                    </h3>
                    <p class="mm-dlg-section__hint">
                        הזן את מספר הסים החדש. הבקשה תישלח לספק <strong>{{ operatorName }}</strong>.
                    </p>

                    <div class="oa-mode-indicator" :class="'oa-mode-indicator--' + currentActionResponseMode">
                        <v-icon size="16" :color="currentActionResponseMode === 'immediate' ? '#0a7a3a' : '#5a4ab8'">
                            {{ currentActionResponseMode === 'immediate' ? 'mdi-lightning-bolt' : 'mdi-clock-outline' }}
                        </v-icon>
                        <span v-if="currentActionResponseMode === 'immediate'">
                            <strong>פעולה מיידית</strong> — התוצאה תתקבל מיד עם השליחה
                        </span>
                        <span v-else>
                            <strong>ממתין לאישור הספק</strong> — התוצאה תתקבל בהמשך באמצעות callback
                        </span>
                    </div>

                    <div class="oa-current-card">
                        <div class="oa-current-card__row">
                            <v-icon size="16" color="#94a3b8">mdi-sim-outline</v-icon>
                            <span class="oa-current-card__label">מספר סים נוכחי:</span>
                            <span class="oa-current-card__value">{{ itemEdited.sim_number || '—' }}</span>
                        </div>
                    </div>

                    <!-- ─── OTP gate: לפני שליחת הקוד לא מציגים את שדה הסים ─── -->
                    <div v-if="requiresOtp && !otpSent" class="oa-otp-box">
                        <div class="oa-otp-box__icon">
                            <v-icon size="26" color="#5a4ab8">mdi-shield-key-outline</v-icon>
                        </div>
                        <div class="oa-otp-box__content">
                            <div class="oa-otp-box__title">נדרש אימות בקוד חד־פעמי (OTP)</div>
                            <div class="oa-otp-box__text">
                                ספק <strong>{{ operatorName }}</strong> דורש אימות לפני ביצוע החלפת הסים.
                                לחץ על "שלח קוד אימות", ולאחר קבלת הקוד ניתן יהיה להזין את מספר הסים החדש
                                יחד עם הקוד שהתקבל.
                            </div>
                            <button type="button" class="mm-dlg-btn mm-dlg-btn--primary oa-otp-box__btn"
                                @click="sendOtp" :disabled="progressShow">
                                <v-icon small color="white">mdi-message-badge-outline</v-icon>
                                שלח קוד אימות
                            </button>
                        </div>
                    </div>

                    <v-form v-if="showActionFields" ref="simForm" @submit.prevent>
                        <div v-if="requiresOtp" class="oa-otp-sent">
                            <v-icon size="16" color="#0a7a3a">mdi-check-circle-outline</v-icon>
                            <span class="oa-otp-sent__text">
                                קוד האימות נשלח. הזן את הקוד שהתקבל יחד עם מספר הסים החדש.
                            </span>
                            <button type="button" class="oa-otp-resend" @click="sendOtp"
                                :disabled="progressShow || otpCooldown > 0">
                                <v-icon size="14">mdi-refresh</v-icon>
                                {{ otpCooldown > 0 ? `שליחה חוזרת (${otpCooldown})` : 'שליחה חוזרת' }}
                            </button>
                        </div>

                        <v-row>
                            <v-col cols="12" sm="8">
                                <v-text-field label="מספר סים חדש" type="number" hide-spin-buttons outlined dense
                                    class="mm-ltr-number" prepend-inner-icon="mdi-sim-outline" v-model="newSimNumber"
                                    required :rules="simRules" autofocus />
                            </v-col>
                            <v-col v-if="requiresOtp" cols="12" sm="4">
                                <v-text-field label="קוד אימות (OTP)" type="number" hide-spin-buttons outlined dense
                                    class="mm-ltr-number" prepend-inner-icon="mdi-shield-key-outline" v-model="otpCode"
                                    required :rules="otpRules"
                                    :maxlength="currentActionOtpConfig && currentActionOtpConfig.length" />
                            </v-col>
                        </v-row>
                    </v-form>
                </div>

                <!-- ============ STEP 2B: refresh subscription ============ -->
                <div v-else-if="currentStep === 'action' && currentAction === 'refresh_manui'" class="mm-dlg-section">
                    <h3 class="mm-dlg-section__title">
                        <span class="mm-dlg-section__bar"></span>
                        ריענון מנוי אצל הספק
                    </h3>
                    <p class="mm-dlg-section__hint">
                        פעולה זו תשלח בקשה לספק <strong>{{ operatorName }}</strong> לסנכרון ועדכון
                        נתוני המנוי במערכותיו.
                    </p>

                    <div class="oa-mode-indicator" :class="'oa-mode-indicator--' + currentActionResponseMode">
                        <v-icon size="16" :color="currentActionResponseMode === 'immediate' ? '#0a7a3a' : '#5a4ab8'">
                            {{ currentActionResponseMode === 'immediate' ? 'mdi-lightning-bolt' : 'mdi-clock-outline' }}
                        </v-icon>
                        <span v-if="currentActionResponseMode === 'immediate'">
                            <strong>פעולה מיידית</strong> — התוצאה תתקבל מיד עם השליחה
                        </span>
                        <span v-else>
                            <strong>ממתין לאישור הספק</strong> — התוצאה תתקבל בהמשך באמצעות callback
                        </span>
                    </div>

                    <div v-if="operatorCode === 'hotMobile'" class="oa-info-banner" style="margin-bottom: 14px;">
                        <div class="oa-info-banner__icon">
                            <v-icon size="18" color="#3F51B5">mdi-information-outline</v-icon>
                        </div>
                        <div class="oa-info-banner__text">
                            הריענון מבוצע אצל הספק ברקע ויושלם בדקה הקרובה. אין צורך להמתין במסך זה.
                        </div>
                    </div>
                    
                    <div class="oa-confirm-box">
                        <div class="oa-confirm-box__icon">
                            <v-icon size="28" color="#c0691c">mdi-refresh</v-icon>
                        </div>
                        <div class="oa-confirm-box__content">
                            <div class="oa-confirm-box__title">לאשר ביצוע ריענון?</div>
                            <div class="oa-confirm-box__details">
                                <div class="oa-confirm-box__row">
                                    <v-icon size="14" color="#94a3b8">mdi-sim</v-icon>
                                    <span class="oa-confirm-box__label">מספר מנוי:</span>
                                    <span class="oa-confirm-box__value">{{ itemEdited.manui_number || '—' }}</span>
                                </div>
                                <div class="oa-confirm-box__row">
                                    <v-icon size="14" color="#94a3b8">mdi-access-point</v-icon>
                                    <span class="oa-confirm-box__label">ספק:</span>
                                    <span class="oa-confirm-box__value">{{ operatorName }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </v-card-text>

            <!-- ─────────────────── FOOTER ─────────────────── -->
            <div class="mm-dlg-foot">
                <v-progress-linear v-if="progressShow" indeterminate color="#0d2c6d" height="3"
                    class="mm-dlg-progress" />
                <div class="mm-dlg-foot__actions">
                    <button type="button" class="mm-dlg-btn mm-dlg-btn--ghost" @click="closeDialog"
                        :disabled="progressShow">
                        <v-icon small>mdi-close</v-icon>
                        ביטול
                    </button>
                    <button v-if="currentStep === 'action' && showActionFields" type="button"
                        class="mm-dlg-btn mm-dlg-btn--primary" @click="executeAction" :disabled="progressShow">
                        <v-icon small color="white">{{ currentActionConfig.submitIcon }}</v-icon>
                        {{ currentActionConfig.submitLabel }}
                    </button>
                </div>
            </div>
        </v-card>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </v-dialog>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'

// ───────────────────────────────────────────────────────────────
// הגדרת הפעולות + הספקים שתומכים בכל פעולה.
// להוספת פעולה חדשה - להוסיף ערך חדש כאן ולהוסיף סלוט בטמפלייט.
// ───────────────────────────────────────────────────────────────
const ACTIONS_CONFIG = {
    replace_sim: {
        key: 'replace_sim',
        title: 'החלפת סים',
        description: 'שליחת בקשה לספק להחליף את הסים הנוכחי בסים חדש',
        icon: 'mdi-sim-outline',
        iconBg: 'rgba(63, 81, 181, 0.10)',
        iconColor: '#3F51B5',
        allowedOperators: ['partner', 'hotMobile', 'clickCallMobile'],
        // allowedOperators: ['hotMobile', 'clickCallMobile'],
        submitLabel: 'שלח בקשה לספק',
        submitIcon: 'mdi-send-outline',
        endpoint: '/manuim/operator/replace_sim',
        successMessage: 'בקשת החלפת סים נשלחה לספק. ההמתנה לאישור.',
        immediateSuccessMessage: 'הסים הוחלף בהצלחה אצל הספק.',
        requiresValidCurrentSim: true,
        // ───────────────────────────────────────────────
        // ספקים שדורשים אימות OTP לפני ביצוע הפעולה.
        // מפתח = קוד הספק, ערך = הגדרות שליחת הקוד.
        // להוספת ספק שדורש OTP - פשוט להוסיף מפתח כאן.
        // ───────────────────────────────────────────────
        otpByOperator: {
            partner: {
                endpoint: '/manuim/operator/send_otp_replace_sim',
                length: 6,          // אורך הקוד הצפוי (null = בלי בדיקת אורך)
                resendCooldown: 30, // שניות עד שניתן לשלוח קוד שוב
                sentMessage: 'קוד אימות נשלח בהצלחה.',
            },
        },
        // Response mode per operator: 'immediate' (sync result) or 'callback' (async via CB)
        responseModeByOperator: {
            partner: 'callback',
            hotMobile: 'immediate',
            clickCallMobile: 'immediate',
        },
    },
    refresh_manui: {
        key: 'refresh_manui',
        title: 'ריענון מנוי',
        description: 'בקשה מהספק לסנכרון ועדכון נתוני המנוי',
        icon: 'mdi-refresh',
        iconBg: 'rgba(253, 183, 33, 0.14)',
        iconColor: '#c0691c',
        allowedOperators: ['partner', 'clickCallMobile', 'hotMobile'],
        submitLabel: 'בצע ריענון',
        submitIcon: 'mdi-refresh',
        endpoint: '/manuim/operator/refresh_manui',
        successMessage: 'בקשת ריענון נשלחה לספק. ההמתנה לתשובה.',
        immediateSuccessMessage: 'המנוי רוענן בהצלחה אצל הספק.',
        requiresValidCurrentSim: false,
        responseModeByOperator: {
            partner: 'immediate',
            clickCallMobile: 'immediate',
            hotMobile: 'immediate',
        },
    },
};

export default {
    props: {
        itemToEdit: Object,
        value: { type: Boolean, default: false },
    },
    components: { SnackBar },
    data: () => ({
        progressShow: false,
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        itemEdited: {
            _id: "",
            sim_number: "",
            manui_number: "",
            plan_id: { operating_company: "" },
        },
        currentStep: 'select',     // 'select' | 'action'
        currentAction: null,        // key from ACTIONS_CONFIG
        newSimNumber: "",
        // ─── OTP state ───
        otpSent: false,
        otpCode: "",
        otpCooldown: 0,
        otpCooldownTimer: null,
        operatorNames: {
            clickCallMobile: "קליקול מובייל",
            hotMobile: "הוט מובייל",
            partner: "פרטנר",
        },
    }),
    computed: {
        openMode: {
            get() { return this.value },
            set(v) { return this.$emit('input', v) }
        },
        operatorCode() {
            return this.itemEdited.plan_id?.operating_company || '';
        },
        operatorName() {
            return this.operatorNames[this.operatorCode] || this.operatorCode || '—';
        },
        availableActions() {
            return Object.values(ACTIONS_CONFIG).map(action => {
                const responseMode = action.responseModeByOperator?.[this.operatorCode] || null;

                // 1. check if operator supports this action
                const operatorSupported = action.allowedOperators.includes(this.operatorCode);
                if (!operatorSupported) {
                    return {
                        ...action,
                        enabled: false,
                        disabledReason: `לא זמין ל${this.operatorName}`,
                        responseMode,
                    };
                }

                // 2. action-specific prerequisites
                if (action.requiresValidCurrentSim && !this.isCurrentSimValid) {
                    return {
                        ...action,
                        enabled: false,
                        disabledReason: this.currentSimInvalidReason,
                        responseMode,
                    };
                }

                return { ...action, enabled: true, disabledReason: null, responseMode };
            });
        },
        currentActionResponseMode() {
            return this.currentActionConfig?.responseModeByOperator?.[this.operatorCode] || null;
        },
        isCurrentSimValid() {
            const sim = this.itemEdited.sim_number;
            if (!sim) return false;
            if (this.operatorCode === 'partner') {
                return /^8997201/.test(sim) && sim.length === 20;
            }
            if (this.operatorCode === 'hotMobile') {
                return /^8997207/.test(sim) && sim.length === 19;
            }
            if (this.operatorCode === 'clickCallMobile') {
                return true; // no specific format validation
            }
            return false;
        },
        currentSimInvalidReason() {
            const sim = this.itemEdited.sim_number;
            if (!sim) return 'אין מספר סים רשום במנוי';
            if (this.operatorCode === 'partner') {
                if (!/^8997201/.test(sim)) return 'הסים הנוכחי אינו תואם לפרטנר';
                if (sim.length !== 20) return 'אורך הסים הנוכחי אינו תקין לפרטנר';
            }
            if (this.operatorCode === 'hotMobile') {
                if (!/^8997207/.test(sim)) return 'הסים הנוכחי אינו תואם להוט מובייל';
                if (sim.length !== 19) return 'אורך הסים הנוכחי אינו תקין להוט מובייל';
            }
            return 'מספר הסים הנוכחי אינו תקין';
        },
        hasCurrentSimIssue() {
            // show warning only when (a) current sim is invalid AND
            // (b) at least one supported action requires a valid current sim
            if (this.isCurrentSimValid) return false;
            return Object.values(ACTIONS_CONFIG).some(
                a => a.requiresValidCurrentSim && a.allowedOperators.includes(this.operatorCode)
            );
        },
        currentActionConfig() {
            return ACTIONS_CONFIG[this.currentAction] || {};
        },
        // הגדרות ה-OTP של הפעולה הנוכחית עבור הספק הנוכחי (null = לא נדרש OTP)
        currentActionOtpConfig() {
            return this.currentActionConfig?.otpByOperator?.[this.operatorCode] || null;
        },
        requiresOtp() {
            return !!this.currentActionOtpConfig;
        },
        // האם להציג את שדות הטופס (סים חדש + קוד)
        showActionFields() {
            return !this.requiresOtp || this.otpSent;
        },
        otpRules() {
            const len = this.currentActionOtpConfig?.length || null;
            return [
                v => !!v || 'שדה חובה',
                v => /^\d+$/.test(v || '') || 'הקוד מכיל ספרות בלבד',
                v => !len || (v || '').length === len || `הקוד מכיל ${len} ספרות`,
            ];
        },
        simRules() {
            return [
                v => !!v || 'שדה חובה',
                v => this.validateSimStart(v) || `מספר הסים שהזנת אינו תואם לספק ${this.operatorName}`,
                v => this.validateSimLength(v),
                v => v !== this.itemEdited.sim_number || 'מספר הסים החדש זהה לקיים',
            ];
        },
    },
    methods: {
        selectAction(action) {
            if (!action.enabled) return;
            this.currentAction = action.key;
            this.currentStep = 'action';
            this.newSimNumber = "";
            this.resetOtpState();
            this.$nextTick(() => {
                if (this.$refs.simForm) this.$refs.simForm.resetValidation();
            });
        },
        backToSelect() {
            this.currentStep = 'select';
            this.currentAction = null;
            this.resetOtpState();
        },
        resetOtpState() {
            this.otpSent = false;
            this.otpCode = "";
            this.otpCooldown = 0;
            if (this.otpCooldownTimer) {
                clearInterval(this.otpCooldownTimer);
                this.otpCooldownTimer = null;
            }
        },
        startOtpCooldown() {
            const seconds = this.currentActionOtpConfig?.resendCooldown || 0;
            if (!seconds) return;
            this.otpCooldown = seconds;
            if (this.otpCooldownTimer) clearInterval(this.otpCooldownTimer);
            this.otpCooldownTimer = setInterval(() => {
                this.otpCooldown--;
                if (this.otpCooldown <= 0) {
                    clearInterval(this.otpCooldownTimer);
                    this.otpCooldownTimer = null;
                }
            }, 1000);
        },
        // שליחת קוד אימות לספק (כרגע נדרש רק ל-partner בהחלפת סים)
        async sendOtp() {
            const otpCfg = this.currentActionOtpConfig;
            if (!otpCfg) return;

            const payload = {
                manuiId: this.itemEdited._id,
                operator: this.itemEdited.plan_id.operating_company,
            };

            this.progressShow = true;
            try {
                const myJSON = JSON.stringify(payload);
                const token = localStorage.getItem("token");
                const api = process.env.VUE_APP_BASE_URL + otpCfg.endpoint;
                const res = await fetch(api, ApiServices.requestOptions("POST", myJSON, token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar(
                        jsonObject?.message || jsonObject || "אירעה שגיאה בשליחת קוד האימות",
                        "red"
                    );
                    return;
                }

                this.otpSent = true;
                this.otpCode = "";
                this.startOtpCooldown();
                this.showSnackBar(jsonObject?.message || otpCfg.sentMessage || "קוד אימות נשלח.", "green");
                this.$nextTick(() => {
                    if (this.$refs.simForm) this.$refs.simForm.resetValidation();
                });
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("שגיאה: " + error, "red");
            }
        },
        closeDialog() {
            if (this.progressShow) return;
            this.openMode = false;
        },
        async executeAction() {
            if (!this.currentAction) return;
            const cfg = this.currentActionConfig;

            // build payload per action
            const payload = { manuiId: this.itemEdited._id, operator: this.itemEdited.plan_id.operating_company };

            if (this.currentAction === 'replace_sim') {
                // אם הספק דורש OTP - חייבים לשלוח קוד לפני ביצוע הפעולה
                if (this.requiresOtp && !this.otpSent) {
                    this.showSnackBar("יש לשלוח קוד אימות לפני ביצוע ההחלפה", "red");
                    return;
                }
                if (!this.$refs.simForm || !this.$refs.simForm.validate()) return;
                payload.newSim = this.newSimNumber;
                if (this.requiresOtp) payload.otp = this.otpCode;
            }

            this.progressShow = true;
            try {
                const myJSON = JSON.stringify(payload);
                const token = localStorage.getItem("token");
                const api = process.env.VUE_APP_BASE_URL + cfg.endpoint;
                const res = await fetch(api, ApiServices.requestOptions("POST", myJSON, token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar(
                        jsonObject?.message || jsonObject || "אירעה שגיאה בשליחת הבקשה לספק",
                        "red"
                    );
                    return;
                }

                // ───────────────────────────────────────────────
                // Two possible server response modes:
                //   1. Immediate completion (hotMobile always; clickCallMobile always;
                //      partner for refresh_manui):
                //        { completed: true, updatedManui: {...}, message? }
                //      → update manui in parent immediately.
                //
                //   2. Pending callback (currently only partner for replace_sim):
                //        { completed: false, message? }
                //      → no manui update; user waits for CB.
                // ───────────────────────────────────────────────
                if (jsonObject.completed && jsonObject.updatedManui) {
                    const msg = jsonObject.message || cfg.immediateSuccessMessage;
                    this.$emit("itemEdited", jsonObject.updatedManui, msg, "green");
                } else {
                    const msg = jsonObject.message || cfg.successMessage;
                    this.$emit("itemEdited", "", msg, "green");
                }
                this.openMode = false;

            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("שגיאה: " + error, "red");
            }
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },
        validateSimStart(value) {
            if (this.operatorCode === "hotMobile") {
                const validStartDigits = /^8997207/;
                return validStartDigits.test(value);
            } else if (this.operatorCode === "partner") {
                const validStartDigits = /^8997201/;
                return validStartDigits.test(value);
            } else if (this.operatorCode === "clickCallMobile") {
                return true;
            }
        },
        validateSimLength(v) {
            if (this.operatorCode === 'partner' && v.length !== 20) {
                return 'מספר סים חייב להכיל בדיוק 20 תווים עבור Partner';
            } else if (this.operatorCode === 'hotMobile' && v.length !== 19) {
                return 'מספר סים חייב להכיל בדיוק 19 תווים עבור Hot Mobile';
            }
            return true;
        },
    },
    watch: {
        // איפוס מלא בכל פתיחה/סגירה של הדיאלוג (כולל מצב ה-OTP)
        value(newVal) {
            if (!newVal) {
                this.currentStep = 'select';
                this.currentAction = null;
                this.newSimNumber = "";
            }
            this.resetOtpState();
        },
    },
    mounted() {
        this.itemEdited = JSON.parse(JSON.stringify(this.itemToEdit));
    },
    beforeDestroy() {
        if (this.otpCooldownTimer) clearInterval(this.otpCooldownTimer);
    },
}
</script>

<style scoped>
/* ─────────────────────── DIALOG BASE (aligned with EditSimDialog) ─────────────────────── */
.mm-dialog {
    border-radius: 20px !important;
    overflow: hidden;
}

.mm-dlg-card {
    --mm-navy: #0d2c6d;
    --mm-indigo: #3F51B5;
    --mm-purple: #8066f0;
    --mm-gold: #fdb721;
    --mm-border: #eef2f7;
    --mm-text: #0f172a;
    --mm-text-soft: #475569;
    --mm-text-mute: #94a3b8;
    font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
    direction: rtl;
    border-radius: 20px !important;
    overflow: hidden;
    background: #ffffff !important;
}

/* ─────────────────────── HEADER ─────────────────────── */
.mm-dlg-head {
    position: relative;
    background:
        radial-gradient(800px 200px at 90% -40%, rgba(253, 183, 33, 0.08), transparent 60%),
        linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
    padding: 18px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.mm-dlg-head__main {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.mm-dlg-head__icon {
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.22);
    display: grid;
    place-items: center;
}

.mm-dlg-eyebrow {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 1px;
}

.mm-dlg-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mm-dlg-head__pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 34px;
    padding: 7px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
}

.mm-dlg-close {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #fff;
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: background 0.2s, transform 0.2s;
}

.mm-dlg-close:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: rotate(90deg);
}

/* ─────────────────────── BREADCRUMB / BACK BAR ─────────────────────── */
.oa-breadcrumb {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 24px;
    background: #f8fafc;
    border-bottom: 1px solid var(--mm-border);
}

.oa-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid var(--mm-border);
    background: #fff;
    color: var(--mm-text-soft);
    font-family: inherit;
    font-size: 0.84rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.18s cubic-bezier(0.22, 1, 0.36, 1);
}

.oa-back-btn:hover:not(:disabled) {
    color: var(--mm-navy);
    border-color: var(--mm-navy);
    transform: translateX(2px);
}

.oa-back-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.oa-breadcrumb__manui {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.82rem;
    color: var(--mm-text-mute);
    font-weight: 700;
}

/* ─────────────────────── BODY ─────────────────────── */
.mm-dlg-body {
    padding: 22px 28px 14px !important;
    background: #fbfcfe;
    max-height: 70vh;
    overflow-y: auto;
}

/* ─────────────────────── INFO BANNER ─────────────────────── */
.oa-select {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.oa-info-banner {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(63, 81, 181, 0.06);
    border: 1px solid rgba(63, 81, 181, 0.18);
}

.oa-info-banner__icon {
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid rgba(63, 81, 181, 0.15);
    display: grid;
    place-items: center;
}

.oa-info-banner__text {
    font-size: 0.86rem;
    line-height: 1.55;
    color: var(--mm-text-soft);
    font-weight: 600;
}

/* ─────────────────────── WARNING BANNER (current sim issue) ─────────────────────── */
.oa-warning-banner {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    padding: 12px 14px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(253, 183, 33, 0.08), rgba(253, 183, 33, 0.03));
    border: 1px solid rgba(253, 183, 33, 0.30);
}

.oa-warning-banner__icon {
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid rgba(253, 183, 33, 0.32);
    display: grid;
    place-items: center;
}

.oa-warning-banner__content {
    flex: 1;
    min-width: 0;
}

.oa-warning-banner__title {
    font-size: 0.88rem;
    font-weight: 800;
    color: #c0691c;
    margin-bottom: 3px;
}

.oa-warning-banner__text {
    font-size: 0.82rem;
    line-height: 1.55;
    color: var(--mm-text-soft);
    font-weight: 600;
}

/* ─────────────────────── ACTION CARDS ─────────────────────── */
.oa-actions-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.oa-action-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 18px;
    text-align: right;
    background: #ffffff;
    border: 1px solid var(--mm-border);
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.22s cubic-bezier(0.22, 1, 0.36, 1);
    font-family: inherit;
    overflow: hidden;
}

.oa-action-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(13, 44, 109, 0.025), transparent 60%);
    opacity: 0;
    transition: opacity 0.22s;
    pointer-events: none;
}

.oa-action-card:hover:not(.oa-action-card--disabled) {
    border-color: var(--mm-navy);
    transform: translateY(-1px);
    box-shadow: 0 14px 30px -18px rgba(13, 44, 109, 0.30);
}

.oa-action-card:hover:not(.oa-action-card--disabled)::before {
    opacity: 1;
}

.oa-action-card--disabled {
    background: #fafbfc;
    opacity: 0.7;
    cursor: not-allowed;
}

.oa-action-card__icon {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.oa-action-card:hover:not(.oa-action-card--disabled) .oa-action-card__icon {
    transform: scale(1.06) rotate(-3deg);
}

.oa-action-card__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.oa-action-card__head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.oa-action-card__title {
    font-size: 0.98rem;
    font-weight: 800;
    color: var(--mm-text);
}

.oa-action-card__desc {
    font-size: 0.84rem;
    color: var(--mm-text-soft);
    line-height: 1.5;
}

.oa-action-card__badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.02em;
}

.oa-action-card__badge--enabled {
    background: rgba(13, 138, 13, 0.10);
    color: rgb(13, 138, 13);
}

.oa-action-card__badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgb(13, 138, 13);
    box-shadow: 0 0 0 3px rgba(13, 138, 13, 0.18);
}

.oa-action-card__badge--disabled {
    background: rgba(206, 18, 18, 0.08);
    color: rgb(180, 35, 24);
}

.oa-action-card__badge--immediate {
    background: rgba(10, 122, 58, 0.10);
    color: #0a7a3a;
    gap: 4px;
}

.oa-action-card__badge--callback {
    background: rgba(90, 74, 184, 0.10);
    color: #5a4ab8;
    gap: 4px;
}

/* ─────────────────────── MODE INDICATOR (in action step) ─────────────────────── */
.oa-mode-indicator {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 10px 13px;
    margin-bottom: 14px;
    border-radius: 10px;
    font-size: 0.84rem;
    line-height: 1.45;
    color: var(--mm-text-soft);
    font-weight: 600;
}

.oa-mode-indicator strong {
    font-weight: 800;
}

.oa-mode-indicator--immediate {
    background: rgba(10, 122, 58, 0.06);
    border: 1px solid rgba(10, 122, 58, 0.22);
}

.oa-mode-indicator--immediate strong {
    color: #0a7a3a;
}

.oa-mode-indicator--callback {
    background: rgba(90, 74, 184, 0.06);
    border: 1px solid rgba(90, 74, 184, 0.22);
}

.oa-mode-indicator--callback strong {
    color: #5a4ab8;
}

.oa-action-card__chev {
    flex: 0 0 26px;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: #f1f5f9;
    display: grid;
    place-items: center;
    color: var(--mm-text-mute);
    transition: all 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.oa-action-card:hover:not(.oa-action-card--disabled) .oa-action-card__chev {
    background: var(--mm-navy);
    color: #fff;
    transform: translateX(-3px);
}

/* ─────────────────────── ACTION FORM SECTION ─────────────────────── */
.mm-dlg-section {
    padding: 18px 20px 8px;
    background: #ffffff;
    border: 1px solid var(--mm-border);
    border-radius: 14px;
    box-shadow: 0 12px 28px -24px rgba(15, 23, 42, 0.35);
}

.mm-dlg-section__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.95rem;
    font-weight: 800;
    color: var(--mm-navy);
    margin: 0 0 10px;
}

.mm-dlg-section__bar {
    width: 3px;
    height: 16px;
    background: linear-gradient(180deg, var(--mm-navy), var(--mm-purple));
    border-radius: 2px;
}

.mm-dlg-section__hint {
    color: var(--mm-text-soft);
    font-size: 0.86rem;
    line-height: 1.7;
    margin: 0 0 16px;
}

.mm-dlg-section__hint strong {
    color: var(--mm-navy);
    font-weight: 800;
}

/* ─────────────────────── CURRENT VALUE CARD ─────────────────────── */
.oa-current-card {
    padding: 12px 14px;
    margin-bottom: 14px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e3e8ef;
}

.oa-current-card__row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.oa-current-card__label {
    font-size: 0.84rem;
    color: var(--mm-text-soft);
    font-weight: 700;
}

.oa-current-card__value {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.9rem;
    font-weight: 800;
    color: var(--mm-text);
    direction: ltr;
    letter-spacing: 0.02em;
    margin-right: auto;
}

/* ─────────────────────── CONFIRM BOX (refresh) ─────────────────────── */
.oa-confirm-box {
    display: flex;
    gap: 16px;
    padding: 18px;
    background: linear-gradient(135deg, rgba(253, 183, 33, 0.06), rgba(253, 183, 33, 0.02));
    border: 1px solid rgba(253, 183, 33, 0.28);
    border-radius: 14px;
}

.oa-confirm-box__icon {
    flex: 0 0 52px;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(253, 183, 33, 0.32);
    display: grid;
    place-items: center;
}

.oa-confirm-box__content {
    flex: 1;
    min-width: 0;
}

.oa-confirm-box__title {
    font-size: 1rem;
    font-weight: 800;
    color: var(--mm-text);
    margin-bottom: 12px;
}

.oa-confirm-box__details {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.oa-confirm-box__row {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.86rem;
}

.oa-confirm-box__label {
    color: var(--mm-text-soft);
    font-weight: 700;
}

.oa-confirm-box__value {
    color: var(--mm-text);
    font-weight: 800;
}

/* ─────────────────────── OTP ─────────────────────── */
.oa-otp-box {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 16px 18px;
    margin-bottom: 6px;
    border: 1px solid #e0dbf7;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(128, 102, 240, 0.07) 0%, rgba(128, 102, 240, 0.02) 100%);
}

.oa-otp-box__icon {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: rgba(128, 102, 240, 0.12);
}

.oa-otp-box__title {
    font-size: 0.95rem;
    font-weight: 800;
    color: var(--mm-text);
    margin-bottom: 4px;
}

.oa-otp-box__text {
    font-size: 0.85rem;
    line-height: 1.6;
    color: var(--mm-text-soft);
}

.oa-otp-box__btn {
    margin-top: 12px;
}

.oa-otp-sent {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 9px 12px;
    margin-bottom: 14px;
    border: 1px solid #cfe9d8;
    border-radius: 10px;
    background: rgba(10, 122, 58, 0.05);
}

.oa-otp-sent__text {
    font-size: 0.83rem;
    font-weight: 700;
    color: #0a7a3a;
}

.oa-otp-resend {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-inline-start: auto;
    padding: 4px 10px;
    border: 1px solid #e3e8ef;
    border-radius: 8px;
    background: #fff;
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--mm-text-soft);
    cursor: pointer;
    transition: all 0.2s;
}

.oa-otp-resend:hover:not(:disabled) {
    background: #f8fafc;
    color: var(--mm-navy);
    border-color: #c7d2e0;
}

.oa-otp-resend:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

/* ─────────────────────── INPUT LTR + STYLING ─────────────────────── */
.mm-ltr-number input,
.mm-ltr-number .v-input__slot input {
    direction: ltr;
    text-align: left;
    letter-spacing: 0.02em;
}

::v-deep .v-text-field--outlined fieldset {
    border-color: #e3e8ef !important;
    transition: border-color 0.2s, box-shadow 0.2s;
}

::v-deep .v-text-field--outlined:hover fieldset {
    border-color: #c7d2e0 !important;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
    border-color: var(--mm-navy) !important;
    border-width: 2px !important;
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.06);
}

::v-deep .v-input .v-label,
::v-deep .v-input input,
::v-deep .v-input textarea,
::v-deep .v-select__selection {
    font-family: inherit;
}

::v-deep .v-input__prepend-inner {
    margin-top: 9px !important;
}

::v-deep .v-input__prepend-inner .v-icon {
    color: var(--mm-text-mute) !important;
}

::v-deep .v-input--is-focused .v-input__prepend-inner .v-icon {
    color: var(--mm-navy) !important;
}

/* ─────────────────────── FOOTER ─────────────────────── */
.mm-dlg-foot {
    background: #ffffff;
    border-top: 1px solid var(--mm-border);
}

.mm-dlg-progress {
    display: block;
}

.mm-dlg-foot__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 22px;
}

.mm-dlg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 22px;
    border-radius: 10px;
    border: none;
    font-family: inherit;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.mm-dlg-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.mm-dlg-btn--ghost {
    background: transparent;
    color: var(--mm-text-soft);
    border: 1px solid #e3e8ef;
}

.mm-dlg-btn--ghost:hover:not(:disabled) {
    background: #f8fafc;
    color: var(--mm-text);
}

.mm-dlg-btn--primary {
    background: linear-gradient(135deg, var(--mm-navy) 0%, #142f78 100%);
    color: #fff;
    box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.45);
}

.mm-dlg-btn--primary:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.08);
}

/* ─────────────────────── RESPONSIVE ─────────────────────── */
@media (max-width: 600px) {
    .mm-dlg-head {
        padding: 14px 18px;
    }

    .mm-dlg-title {
        font-size: 1rem;
    }

    .mm-dlg-head__pill {
        display: none;
    }

    .mm-dlg-body {
        padding: 18px 16px 8px !important;
    }

    .oa-breadcrumb {
        padding: 10px 16px;
    }

    .oa-action-card {
        padding: 14px;
        gap: 12px;
    }

    .oa-action-card__icon {
        flex: 0 0 42px;
        width: 42px;
        height: 42px;
    }

    .oa-action-card__title {
        font-size: 0.92rem;
    }

    .oa-action-card__desc {
        font-size: 0.8rem;
    }

    .mm-dlg-section {
        padding: 14px 14px 4px;
    }

    .oa-confirm-box {
        flex-direction: column;
        text-align: center;
        padding: 16px;
    }

    .oa-confirm-box__icon {
        margin: 0 auto;
    }

    .oa-confirm-box__details {
        align-items: center;
    }

    .mm-dlg-foot__actions {
        padding: 12px 16px;
    }

    .mm-dlg-btn {
        padding: 9px 16px;
        font-size: 0.85rem;
    }
}
</style>